import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Select, Text, Skeleton, Tabs, Badge, Image, Card, SimpleGrid, Anchor } from '@mantine/core';
import { IconSearch, IconPhoto, IconVideo, IconFileText, IconExternalLink } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import {
  useGoogleAdsAssets,
  useGoogleAdsImages,
  useGoogleAdsVideos,
  useGoogleAdsHeadlines,
  useGoogleAdsDescriptions,
} from '@/hooks/useGoogleAds';
import type { Asset, ImageAsset, VideoAsset, Headline, Description } from '@/types/googleAds';
import { StatusBadge } from '../components/StatusBadge';

interface AssetsTabProps {
  customerId: string;
}

export function AssetsTab({ customerId }: AssetsTabProps) {
  const { data: assets, isLoading: assetsLoading } = useGoogleAdsAssets(customerId);
  const { data: images, isLoading: imagesLoading } = useGoogleAdsImages(customerId);
  const { data: videos, isLoading: videosLoading } = useGoogleAdsVideos(customerId);
  const { data: headlines, isLoading: headlinesLoading } = useGoogleAdsHeadlines(customerId);
  const { data: descriptions, isLoading: descriptionsLoading } = useGoogleAdsDescriptions(customerId);

  const [activeTab, setActiveTab] = useState<string | null>('images');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const isLoading = assetsLoading || imagesLoading || videosLoading || headlinesLoading || descriptionsLoading;

  const filteredAssets = assets?.filter((asset) => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (asset.name && !asset.name.toLowerCase().includes(searchLower)) return false;
      if (asset.textContent && !asset.textContent.toLowerCase().includes(searchLower)) return false;
    }
    if (typeFilter && asset.type !== typeFilter) return false;
    return true;
  });

  const filteredImages = images?.filter((img) => {
    if (search && img.name && !img.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredVideos = videos?.filter((video) => {
    if (search && video.youtubeVideoTitle && !video.youtubeVideoTitle.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredHeadlines = headlines?.filter((h) => {
    if (search && !h.text.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredDescriptions = descriptions?.filter((d) => {
    if (search && !d.text.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const uniqueAssetTypes = [...new Set(assets?.map((a) => a.type) ?? [])];

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Asset suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
          {activeTab === 'all' && (
            <Select
              placeholder="Asset-Typ"
              data={[
                { value: '', label: 'Alle Typen' },
                ...uniqueAssetTypes.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
              ]}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v ?? '')}
              clearable
              w={180}
            />
          )}
        </Group>
      </Paper>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>
            Bilder{' '}
            <Badge size="sm" variant="light" ml={4}>
              {images?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="videos" leftSection={<IconVideo size={16} />}>
            Videos{' '}
            <Badge size="sm" variant="light" ml={4}>
              {videos?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="headlines" leftSection={<IconFileText size={16} />}>
            Headlines{' '}
            <Badge size="sm" variant="light" ml={4}>
              {headlines?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="descriptions" leftSection={<IconFileText size={16} />}>
            Descriptions{' '}
            <Badge size="sm" variant="light" ml={4}>
              {descriptions?.length ?? 0}
            </Badge>
          </Tabs.Tab>
          <Tabs.Tab value="all">
            Alle Assets{' '}
            <Badge size="sm" variant="light" ml={4}>
              {assets?.length ?? 0}
            </Badge>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="images" pt="md">
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }}>
            {filteredImages?.map((img) => (
              <Card key={img.id} withBorder padding="xs">
                <Card.Section>
                  <Image src={img.url} height={120} fit="contain" alt={img.name ?? 'Image'} />
                </Card.Section>
                <Text size="xs" fw={500} mt="xs" lineClamp={1}>
                  {img.name ?? 'Unbenannt'}
                </Text>
                <Group gap="xs" mt={4}>
                  {img.width && img.height && (
                    <Text size="xs" c="dimmed">
                      {img.width}x{img.height}
                    </Text>
                  )}
                  {img.performanceLabel && (
                    <StatusBadge status={img.performanceLabel} type="performance" />
                  )}
                </Group>
                <Text size="xs" c="dimmed" mt={4}>
                  {img.usedIn.length} Verwendung{img.usedIn.length !== 1 ? 'en' : ''}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
          {filteredImages?.length === 0 && (
            <Text c="dimmed" ta="center" py="xl">
              Keine Bilder gefunden
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="videos" pt="md">
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            {filteredVideos?.map((video) => (
              <Card key={video.id} withBorder padding="sm">
                <Group wrap="nowrap">
                  <Image
                    src={`https://img.youtube.com/vi/${video.youtubeVideoId}/mqdefault.jpg`}
                    height={90}
                    width={160}
                    fit="cover"
                    radius="sm"
                    alt={video.youtubeVideoTitle ?? 'Video'}
                  />
                  <div style={{ flex: 1 }}>
                    <Anchor
                      href={`https://www.youtube.com/watch?v=${video.youtubeVideoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      fw={500}
                      lineClamp={2}
                    >
                      {video.youtubeVideoTitle ?? video.youtubeVideoId}
                      <IconExternalLink size={12} style={{ marginLeft: 4 }} />
                    </Anchor>
                    <Group gap="xs" mt={4}>
                      {video.durationMillis && (
                        <Text size="xs" c="dimmed">
                          {Math.floor(video.durationMillis / 60000)}:{String(Math.floor((video.durationMillis % 60000) / 1000)).padStart(2, '0')}
                        </Text>
                      )}
                      {video.performanceLabel && (
                        <StatusBadge status={video.performanceLabel} type="performance" />
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" mt={4}>
                      {video.usedIn.length} Verwendung{video.usedIn.length !== 1 ? 'en' : ''}
                    </Text>
                  </div>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
          {filteredVideos?.length === 0 && (
            <Text c="dimmed" ta="center" py="xl">
              Keine Videos gefunden
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="headlines" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredHeadlines ?? []}
            columns={[
              {
                accessor: 'text',
                title: 'Headline',
                width: 400,
              },
              {
                accessor: 'campaignName',
                title: 'Kampagne',
                width: 200,
              },
              {
                accessor: 'adGroupName',
                title: 'Anzeigengruppe',
                width: 200,
              },
              {
                accessor: 'pinnedPosition',
                title: 'Pinned',
                width: 100,
                render: (h) => h.pinnedPosition ? <Badge size="sm">{h.pinnedPosition}</Badge> : '-',
              },
              {
                accessor: 'performanceLabel',
                title: 'Performance',
                width: 100,
                render: (h) =>
                  h.performanceLabel ? <StatusBadge status={h.performanceLabel} type="performance" /> : '-',
              },
            ]}
            noRecordsText="Keine Headlines gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="descriptions" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredDescriptions ?? []}
            columns={[
              {
                accessor: 'text',
                title: 'Description',
                width: 500,
              },
              {
                accessor: 'campaignName',
                title: 'Kampagne',
                width: 200,
              },
              {
                accessor: 'adGroupName',
                title: 'Anzeigengruppe',
                width: 200,
              },
              {
                accessor: 'pinnedPosition',
                title: 'Pinned',
                width: 100,
                render: (d) => d.pinnedPosition ? <Badge size="sm">{d.pinnedPosition}</Badge> : '-',
              },
              {
                accessor: 'performanceLabel',
                title: 'Performance',
                width: 100,
                render: (d) =>
                  d.performanceLabel ? <StatusBadge status={d.performanceLabel} type="performance" /> : '-',
              },
            ]}
            noRecordsText="Keine Descriptions gefunden"
            minHeight={400}
          />
        </Tabs.Panel>

        <Tabs.Panel value="all" pt="md">
          <DataTable
            withTableBorder
            borderRadius="sm"
            striped
            highlightOnHover
            records={filteredAssets ?? []}
            columns={[
              {
                accessor: 'name',
                title: 'Name',
                width: 250,
                render: (asset) => asset.name ?? asset.textContent ?? '-',
              },
              {
                accessor: 'type',
                title: 'Typ',
                width: 150,
                render: (asset) => (
                  <Badge variant="light" size="sm">
                    {asset.type.replace(/_/g, ' ')}
                  </Badge>
                ),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 100,
                render: (asset) => asset.status ? <StatusBadge status={asset.status} /> : '-',
              },
              {
                accessor: 'performanceLabel',
                title: 'Performance',
                width: 100,
                render: (asset) =>
                  asset.performanceLabel ? (
                    <StatusBadge status={asset.performanceLabel} type="performance" />
                  ) : (
                    '-'
                  ),
              },
              {
                accessor: 'imageWidth',
                title: 'Größe',
                width: 100,
                render: (asset) =>
                  asset.imageWidth && asset.imageHeight
                    ? `${asset.imageWidth}x${asset.imageHeight}`
                    : '-',
              },
              {
                accessor: 'youtubeVideoTitle',
                title: 'Video',
                width: 200,
                render: (asset) =>
                  asset.youtubeVideoId ? (
                    <Anchor
                      href={`https://www.youtube.com/watch?v=${asset.youtubeVideoId}`}
                      target="_blank"
                      size="xs"
                    >
                      {asset.youtubeVideoTitle ?? asset.youtubeVideoId}
                    </Anchor>
                  ) : (
                    '-'
                  ),
              },
            ]}
            noRecordsText="Keine Assets gefunden"
            minHeight={400}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
